import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  InputAdornment,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Divider,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Collapse,
  IconButton,
  Paper,
  RadioGroup,
  Radio,
  FormLabel,
  FormControlLabel,
  Grid
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AccountBalanceWallet, 
  ArrowUpward, 
  CreditCard, 
  Edit,
  CheckCircle,
  Error as ErrorIcon,
  Close,
  AccountBalance,
  SwapHoriz,
  CreditScore,
  PaymentsOutlined,
  Info
} from '@mui/icons-material';
import WalletsAPI from '../api/WalletsAPI';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getCardDetails } from '../api/CardAPI';
import feeManager from '../utils/feeManager';  // Add fee manager

const TopUpDialog = ({ open, onClose, uid, email, firebaseUid, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [san, setSan] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cards, setCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState('');
  const [manualEntry, setManualEntry] = useState(false);
  const [loadingCards, setLoadingCards] = useState(false);
  const [cardDetails, setCardDetails] = useState({});
  const [processStatus, setProcessStatus] = useState('');
  const [processStep, setProcessStep] = useState(0);
  const [transactionComplete, setTransactionComplete] = useState(false);
  const [transactionError, setTransactionError] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);
  const [feeInfo, setFeeInfo] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState('tron');
  const [userBalance, setUserBalance] = useState({ usdt_tron: 0, usdt_eth: 0 });

  // Transaction status messages in English
  const statusMessages = {
    transferring: 'Transferring funds...',
    waiting_blockchain: 'Waiting for blockchain confirmation...',
    loading_card: 'Loading card...',
    completed: 'Completed successfully!'
  };

  // Error messages in English
  const errorMessages = {
    insufficient_balance: 'Insufficient wallet balance',
    card_not_active: 'Card is not active',
    transfer_error: 'Transfer error',
    card_loading_error: 'Card loading error',
    invalid_amount: 'Please enter a valid amount',
    invalid_card: 'Please enter card number (SAN)'
  };

  // Process steps for stepper
  const processSteps = [
    { label: 'Balance Check', icon: <AccountBalance /> },
    { label: 'Transfer Funds', icon: <SwapHoriz /> },
    { label: 'Blockchain Confirmation', icon: <AccountBalanceWallet /> },
    { label: 'Load Card', icon: <CreditScore /> }
  ];

  // Fetch wallet balance
  const fetchWalletBalance = useCallback(async () => {
    if (!open || (!email && !uid)) return;
    
    try {
      // Use email if available, otherwise uid
      const identifier = email || uid;
      const walletStatus = await WalletsAPI.getUserWalletStatus(identifier);
      setWalletBalance(walletStatus.balance.usdt);
      // Set balance for both networks
      setUserBalance({
        usdt_tron: walletStatus.balance.usdt_tron || 0,
        usdt_eth: walletStatus.balance.usdt_eth || 0
      });
    } catch (err) {
      // Failed to fetch wallet balance
    }
  }, [open, email, uid]);

  // Fetch user's cards when dialog opens
  useEffect(() => {
    const fetchUserCards = async () => {
      if (!open || (!firebaseUid && !uid)) return;
      
      try {
        setLoadingCards(true);
        // Use firebaseUid if available, otherwise fallback to uid (for backward compatibility)
        const userIdForCards = firebaseUid || uid;
        const docRef = doc(db, 'users', userIdForCards);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const userCards = data.cards || [];
          setCards(userCards);
          
          // Fetch card details for each card
          const detailsPromises = userCards.map(async (card) => {
            try {
              const details = await getCardDetails(card.san);
              return { san: card.san, details };
            } catch (err) {
              // Failed to fetch details for card
              return { san: card.san, details: null };
            }
          });
          
          const allDetails = await Promise.all(detailsPromises);
          const detailsMap = {};
          allDetails.forEach(({ san, details }) => {
            if (details) {
              detailsMap[san] = details;
            }
          });
          setCardDetails(detailsMap);
        }
      } catch (err) {
        // Failed to fetch user cards
      } finally {
        setLoadingCards(false);
      }
    };
    
    fetchUserCards();
    fetchWalletBalance();
  }, [open, uid, firebaseUid, email, fetchWalletBalance]);

  // Initialize fee manager with real-time subscription
  const [feeStructureVersion, setFeeStructureVersion] = useState(0);

  useEffect(() => {
    let unsubscribeFeeManager;
    let unsubscribeFeeListener;

    const initializeFeeManager = async () => {
      try {
        unsubscribeFeeManager = await feeManager.initialize();

        // Subscribe to fee changes
        unsubscribeFeeListener = feeManager.subscribe((data) => {
          // Trigger re-calculation by updating version
          setFeeStructureVersion(v => v + 1);
        });

      } catch (error) {
      }
    };

    initializeFeeManager();

    return () => {
      if (unsubscribeFeeListener) {
        unsubscribeFeeListener();
      }
      if (unsubscribeFeeManager) {
        unsubscribeFeeManager();
      }
    };
  }, []);

  // Calculate fee when amount changes using fee manager
  useEffect(() => {
    if (amount && parseFloat(amount) > 0 && email) {
      const depositAmount = parseFloat(amount);
      // Use fee manager to calculate fees (supports customer-specific fees)
      const calculatedFee = feeManager.calculateFee(depositAmount, email);
      setFeeInfo(calculatedFee);
    } else {
      setFeeInfo(null);
    }
  }, [amount, email, feeStructureVersion]);

  // Handle card selection
  const handleCardSelect = (event) => {
    const selectedSan = event.target.value;
    setSelectedCard(selectedSan);
    
    if (selectedSan === 'manual') {
      setManualEntry(true);
      setSan('');
    } else if (selectedSan) {
      setManualEntry(false);
      setSan(selectedSan);
    }
  };

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError(errorMessages.invalid_amount);
      return;
    }

    if (!san.trim()) {
      setError(errorMessages.invalid_card);
      return;
    }

    // Check if user has sufficient balance (including fee) based on selected network
    const totalRequired = feeInfo ? feeInfo.totalAmount : parseFloat(amount);
    const networkBalance = selectedNetwork === 'tron' ? userBalance.usdt_tron : userBalance.usdt_eth;
    if (networkBalance !== null && totalRequired > networkBalance) {
      setError(errorMessages.insufficient_balance + ` on ${selectedNetwork.charAt(0).toUpperCase() + selectedNetwork.slice(1)} (Required: $${totalRequired.toFixed(2)})`);
      return;
    }

    try {
      setLoading(true);
      setError('');
      setTransactionComplete(false);
      setTransactionError(false);
      setProcessStep(0);
      
      // Step 1: Balance check
      setProcessStatus(statusMessages.transferring);
      setProcessStep(1);
      
      // Add a small delay to show the step
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Step 2: Transfer funds
      setProcessStep(2);

      const identifier = email || uid;
      // Send total amount (including fee) and card amount separately
      const response = await WalletsAPI.createTopUpRequest(
        identifier,
        feeInfo ? feeInfo.totalAmount : parseFloat(amount),     // Total amount to charge (including fee)
        san,
        selectedNetwork,
        feeInfo ? feeInfo.depositAmount : parseFloat(amount),   // Amount to load on card
        feeInfo ? feeInfo.feeAmount : 0                          // Fee amount (for tracking)
      );
      
      // Step 3: Blockchain confirmation
      setProcessStatus(statusMessages.waiting_blockchain);
      setProcessStep(3);
      
      // Simulate blockchain confirmation time
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Step 4: Loading card
      setProcessStatus(statusMessages.loading_card);
      setProcessStep(4);
      
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      if (response.topupRequest || response.success) {
        setProcessStatus(statusMessages.completed);
        setTransactionComplete(true);
        
        // Wait a bit before closing to show success
        setTimeout(() => {
          onSuccess && onSuccess(response.topupRequest || response);
          handleClose();
        }, 2000);
      } else if (response.warning) {
        // Partial success - funds transferred but card loading failed
        setError(errorMessages.card_loading_error + ' - ' + response.warning);
        setTransactionError(true);
      }
    } catch (err) {
      // Error occurred during top-up
      
      // Determine error type
      let errorMessage = errorMessages.transfer_error;
      
      if (err.message?.includes('Insufficient balance')) {
        errorMessage = errorMessages.insufficient_balance;
      } else if (err.message?.includes('Card is not active')) {
        errorMessage = errorMessages.card_not_active;
      } else if (err.message?.includes('card top-up failed')) {
        errorMessage = errorMessages.card_loading_error;
      }
      
      setError(errorMessage);
      setTransactionError(true);
      setProcessStep(0);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setAmount('');
      setSan('');
      setError('');
      setSelectedCard('');
      setManualEntry(false);
      setProcessStatus('');
      setProcessStep(0);
      setTransactionComplete(false);
      setTransactionError(false);
      onClose();
    }
  };

  // Get display name for a card
  const getCardDisplayName = (card) => {
    const details = cardDetails[card.san];
    const last4 = details?.cardNo ? details.cardNo.slice(-4) : '????';
    const cardName = card.cardName || 'Card';
    const balance = details?.cardBal ? `$${parseFloat(details.cardBal).toFixed(2)}` : '';
    return `${cardName} (•••• ${last4}) ${balance}`;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: '#FFFFFF',
          backdropFilter: 'blur(20px)',
          border: '1px solid #E2E8F0',
          borderRadius: 3,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid #E2E8F0',
        pb: 2,
        background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <motion.div
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          >
            <AccountBalanceWallet sx={{ color: '#3B82F6', fontSize: 28 }} />
          </motion.div>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E293B' }}>
              Top Up Card
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              Load funds to your card instantly
            </Typography>
          </Box>
        </Box>
        {!loading && (
          <IconButton onClick={handleClose} size="small" sx={{ color: '#64748B' }}>
            <Close />
          </IconButton>
        )}
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Alert 
                severity="error" 
                sx={{ mb: 2 }} 
                onClose={() => setError('')}
                icon={<ErrorIcon />}
              >
                {error}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Show wallet balance */}
        {walletBalance !== null && !loading && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1, type: "spring" }}
          >
            <Paper sx={{ 
              p: 3, 
              mb: 3,
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(59, 130, 246, 0.1) 100%)',
              borderRadius: 2,
              border: '1px solid rgba(59, 130, 246, 0.2)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                position: 'absolute',
                top: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)'
              }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#64748B', fontWeight: 500 }}>
                    Available Balance
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#3B82F6' }}>
                    ${selectedNetwork === 'tron' ? userBalance.usdt_tron.toFixed(2) : userBalance.usdt_eth.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748B' }}>
                    USDT ({selectedNetwork === 'tron' ? 'TRON' : 'Ethereum'})
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #3B82F6 0%, #0EA5E9 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)'
                  }}
                >
                  <AccountBalanceWallet sx={{ color: 'white', fontSize: 28 }} />
                </Box>
              </Box>
            </Paper>
          </motion.div>
        )}

        {/* Progress indicator during transaction */}
        <Collapse in={loading && processStep > 0}>
          <Box sx={{ mb: 3 }}>
            <LinearProgress 
              variant="determinate" 
              value={(processStep / processSteps.length) * 100}
              sx={{ 
                height: 8, 
                borderRadius: 4,
                bgcolor: 'var(--bg-secondary)',
                '& .MuiLinearProgress-bar': {
                  background: transactionError 
                    ? 'var(--accent-error)' 
                    : transactionComplete 
                    ? 'var(--accent-success)' 
                    : 'linear-gradient(45deg, var(--primary-blue), var(--primary-sky))',
                  borderRadius: 4
                }
              }}
            />
            <Typography 
              variant="body2" 
              sx={{ 
                mt: 1, 
                textAlign: 'center',
                color: transactionError ? 'var(--accent-error)' : transactionComplete ? 'var(--accent-success)' : 'var(--primary-sky)'
              }}
            >
              {processStatus}
            </Typography>
          </Box>

          <Stepper activeStep={processStep - 1} orientation="vertical" sx={{ mb: 3 }}>
            {processSteps.map((step, index) => (
              <Step key={index}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: index < processStep - 1 
                        ? 'var(--accent-success)' 
                        : index === processStep - 1 
                        ? 'var(--primary-sky)' 
                        : 'var(--bg-secondary)',
                      color: index <= processStep - 1 ? 'white' : 'var(--text-tertiary)',
                      transition: 'all 0.3s ease'
                    }}>
                      {index < processStep - 1 ? <CheckCircle /> : step.icon}
                    </Box>
                  )}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: index <= processStep - 1 ? 'white' : 'var(--text-tertiary)',
                      fontWeight: index === processStep - 1 ? 600 : 400
                    }}
                  >
                    {step.label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Collapse>

        {/* Success animation */}
        <AnimatePresence>
          {transactionComplete && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
            >
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CheckCircle sx={{ fontSize: 80, color: 'var(--accent-success)', mb: 2 }} />
                <Typography variant="h5" sx={{ color: 'var(--accent-success)', fontWeight: 600 }}>
                  {statusMessages.completed}
                </Typography>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Form fields - hide during transaction */}
        <Collapse in={!loading}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ maxLength: 10 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                endAdornment: <InputAdornment position="end">USDT</InputAdornment>,
              }}
              helperText="Enter the amount you want to load"
              error={error === errorMessages.invalid_amount || error === errorMessages.insufficient_balance}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#E2E8F0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3B82F6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3B82F6',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#64748B',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#3B82F6',
                }
              }}
            />
          </motion.div>

          {/* Network Selection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 2, color: '#1E293B', fontWeight: 600 }}>
                Select Payment Network
              </FormLabel>
              <RadioGroup
                value={selectedNetwork}
                onChange={(e) => setSelectedNetwork(e.target.value)}
                sx={{ width: '100%' }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ 
                      p: 2, 
                      border: selectedNetwork === 'tron' ? '2px solid #3B82F6' : '1px solid #E2E8F0',
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: '#3B82F6',
                        bgcolor: 'rgba(59, 130, 246, 0.05)'
                      }
                    }}>
                      <FormControlLabel
                        value="tron"
                        control={<Radio />}
                        label={
                          <Box sx={{ ml: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              TRON Network
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748B' }}>
                              Balance: ${userBalance.usdt_tron.toFixed(2)} USDT
                            </Typography>
                          </Box>
                        }
                        sx={{ width: '100%', m: 0 }}
                      />
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ 
                      p: 2, 
                      border: selectedNetwork === 'ethereum' ? '2px solid #3B82F6' : '1px solid #E2E8F0',
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: '#3B82F6',
                        bgcolor: 'rgba(59, 130, 246, 0.05)'
                      }
                    }}>
                      <FormControlLabel
                        value="ethereum"
                        control={<Radio />}
                        label={
                          <Box sx={{ ml: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              Ethereum Network
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748B' }}>
                              Balance: ${userBalance.usdt_eth.toFixed(2)} USDT
                            </Typography>
                          </Box>
                        }
                        sx={{ width: '100%', m: 0 }}
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </RadioGroup>
            </FormControl>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {loadingCards ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                <FormControl fullWidth>
                  <InputLabel id="card-select-label">Select Card</InputLabel>
                  <Select
                    labelId="card-select-label"
                    value={selectedCard}
                    label="Select Card"
                    onChange={handleCardSelect}
                    startAdornment={
                      <InputAdornment position="start">
                        <CreditCard sx={{ color: '#64748B' }} />
                      </InputAdornment>
                    }
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E2E8F0',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0EA5E9',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0EA5E9',
                      },
                      '& .MuiInputLabel-root': {
                        color: '#64748B',
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#0EA5E9',
                      }
                    }}
                  >
                    {cards.map((card) => (
                      <MenuItem key={card.san} value={card.san}>
                        {getCardDisplayName(card)}
                      </MenuItem>
                    ))}
                    <Divider sx={{ my: 1 }} />
                    <MenuItem value="manual">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Edit fontSize="small" />
                        Enter SAN manually
                      </Box>
                    </MenuItem>
                  </Select>
                  <FormHelperText>
                    {cards.length === 0 
                      ? "No cards found. Enter SAN manually below." 
                      : "Select a card or enter SAN manually"}
                  </FormHelperText>
                </FormControl>

                {(manualEntry || cards.length === 0) && (
                  <TextField
                    fullWidth
                    label="Card Number (SAN)"
                    value={san}
                    onChange={(e) => setSan(e.target.value)}
                    placeholder="Enter the card SAN"
                    helperText="Your card's unique identifier"
                    error={error === errorMessages.invalid_card}
                    inputProps={{ maxLength: 50 }}
                    sx={{
                      mt: 2,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#E2E8F0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#0EA5E9',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#0EA5E9',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#64748B',
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#0EA5E9',
                      }
                    }}
                  />
                )}
              </>
            )}
          </motion.div>

          {selectedCard && selectedCard !== 'manual' && cardDetails[selectedCard] && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Paper sx={{ 
                p: 2.5, 
                background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.05) 0%, rgba(14, 165, 233, 0.1) 100%)',
                borderRadius: 2,
                border: '1px solid rgba(14, 165, 233, 0.2)',
                mb: 2,
                position: 'relative',
                overflow: 'hidden'
              }}>
                <Box sx={{
                  position: 'absolute',
                  top: -10,
                  right: -10,
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(14, 165, 233, 0.2) 0%, transparent 70%)'
                }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CreditCard sx={{ color: '#0EA5E9', fontSize: 32 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', mb: 0.5 }}>
                      Selected Card
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#64748B' }}>
                        Current Balance:
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#0EA5E9' }}>
                        ${parseFloat(cardDetails[selectedCard].cardBal || '0').toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </motion.div>
          )}

            {/* Fee information */}
            {feeInfo && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Paper sx={{
                  p: 3,
                  background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(245, 158, 11, 0.1) 100%)',
                  borderRadius: 2,
                  border: '1px solid rgba(245, 158, 11, 0.2)',
                  mb: 2,
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <Box sx={{
                    position: 'absolute',
                    top: -15,
                    right: -15,
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(245, 158, 11, 0.15) 0%, transparent 70%)'
                  }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: 'rgba(245, 158, 11, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <PaymentsOutlined sx={{ color: '#F59E0B', fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E293B' }}>
                      Fee Summary
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#64748B' }}>
                        Amount to load:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        ${feeInfo.depositAmount.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#64748B' }}>
                        Service fee:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        ${feeInfo.feeAmount.toFixed(2)}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1, borderColor: 'rgba(245, 158, 11, 0.2)' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#1E293B' }}>
                        Total Amount:
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#F59E0B' }}>
                        ${feeInfo.totalAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <Paper sx={{ 
                p: 2.5, 
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(59, 130, 246, 0.1) 100%)',
                borderRadius: 2,
                border: '1px solid rgba(59, 130, 246, 0.2)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2
              }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: 'rgba(59, 130, 246, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Info sx={{ color: '#3B82F6', fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', mb: 0.5 }}>
                    Processing Information
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B' }}>
                    Your top-up request will be processed after blockchain confirmation. The process usually takes 2-5 minutes depending on network congestion.
                  </Typography>
                </Box>
              </Paper>
            </motion.div>
          </Box>
        </Collapse>
      </DialogContent>

      <DialogActions sx={{ 
        p: 3, 
        pt: 2,
        borderTop: '1px solid #E2E8F0',
        background: 'linear-gradient(135deg, #FAFBFC 0%, #F8FAFC 100%)'
      }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          sx={{ 
            color: '#64748B',
            '&:hover': {
              bgcolor: '#F1F5F9'
            }
          }}
        >
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !amount || !san}
          startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <ArrowUpward />}
          size="large"
          sx={{
            px: 4,
            background: 'linear-gradient(45deg, #3B82F6, #0EA5E9)',
            boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
            '&:hover': {
              background: 'linear-gradient(45deg, #2563EB, #0284C7)',
              boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
            },
            '&:disabled': {
              background: '#E2E8F0',
              color: '#94A3B8',
              boxShadow: 'none'
            }
          }}
        >
          {loading ? 'Processing...' : 'Top Up Card'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TopUpDialog;
